import type { FilterBounds, Sort } from 'bnef';
import { createParser } from 'nuqs';

const SORT_PARAM_SEPARATOR = '.';
const ARRAY_PARAM_SEPARATOR = '|';
const RANGE_PARAM_SEPARATOR = '~';

export const parseQueryAsSet = createParser<Set<string>>({
    parse(query) {
        if (!query) return new Set();
        return new Set(query.split(ARRAY_PARAM_SEPARATOR).filter(Boolean));
    },
    serialize(value) {
        if (!value || value.size === 0) return null;
        return Array.from(value).sort().join(ARRAY_PARAM_SEPARATOR);
    },
    eq(a, b) {
        if (a.size !== b.size) {
            return false;
        }
        for (const item of a) {
            if (!b.has(item)) return false;
        }
        return true;
    },
});

export const parseQueryAsSort = createParser<Sort>({
    parse(query) {
        const parts = query.split(SORT_PARAM_SEPARATOR);
        let direction = parts.pop();

        if (!['asc', 'desc'].includes(direction)) {
            // must not be a direction, put it back
            parts.push(direction);
            direction = undefined;
        }
        return {
            name: parts.join(SORT_PARAM_SEPARATOR), // to support deep object search
            direction: direction as Sort['direction'] | undefined,
        };
    },
    serialize(value) {
        return value.direction ? `${value.name}${SORT_PARAM_SEPARATOR}${value.direction}` : value.name;
    },
    eq(a, b) {
        return a.name === b.name && a.direction === b.direction;
    },
});

export const parseQueryAsDateRange = createParser<FilterBounds | string>({
    parse(query): FilterBounds | string | null {
        if (!query) return null;

        // Handle less than: <2030 or <=2030
        if (query.startsWith('<=')) {
            return { lte: query.slice(2) };
        }
        if (query.startsWith('<')) {
            return { lt: query.slice(1) };
        }

        // Handle greater than: >2020 or >=2020
        if (query.startsWith('>=')) {
            return { gte: query.slice(2) };
        }
        if (query.startsWith('>')) {
            return { gt: query.slice(1) };
        }

        // Handle range: 2023~2025 or 2023-01-01~2023-12-31
        if (query.includes(RANGE_PARAM_SEPARATOR)) {
            const [start, end] = query.split(RANGE_PARAM_SEPARATOR);
            return { gte: start, lte: end };
        }

        // Single value or keyword like "last-month", treat as raw string
        return query;
    },
    serialize(value): string | null {
        // Handle string values (keywords like 'last-month')
        if (typeof value === 'string') {
            return value;
        }

        // Handle FilterBounds object
        if (!value || Object.keys(value).length === 0) return null;

        // prefer the "equal" variants
        const from = value.gte || value.gt;
        const to = value.lte || value.lt;

        if (from && to) {
            return `${from}${RANGE_PARAM_SEPARATOR}${to}`;
        }

        // Handle less than
        if (value.lte) {
            return `<=${value.lte}`;
        }
        if (value.lt) {
            return `<${value.lt}`;
        }

        // Handle greater than
        if (value.gte) {
            return `>=${value.gte}`;
        }
        if (value.gt) {
            return `>${value.gt}`;
        }
    },
    eq(a, b): boolean {
        // Handle string comparisons
        if (typeof a === 'string' || typeof b === 'string') {
            return a === b;
        }
        return a.lt === b.lt && a.lte === b.lte && a.gt === b.gt && a.gte === b.gte;
    },
});

export const createNestedTableParams = (
    queryParams: Record<string, any>,
    parents: any[],
    nestingOrder: string[],
    depth: number = nestingOrder.length
): Record<string, any> => {
    const params: Record<string, any> = {
        ...queryParams,
        org: parents[0].id,
    };

    nestingOrder.slice(0, depth).forEach((item, j) => {
        const parentId = parents[j + 1].id ?? undefined;
        const filterValue = queryParams[item];
        // Preserve more specific filters (eg, keep "usa.us-ny|usa.us-ca" filter, ignoring the "USA" parent).
        if (typeof filterValue === 'string' && parentId) {
            const prefix = `${parentId.toLowerCase()}.`;
            const matching = [...parseQueryAsSet.parse(filterValue)].filter((x) => x.toLowerCase().startsWith(prefix));
            params[item] = matching.length > 0 ? parseQueryAsSet.serialize(new Set(matching)) : parentId;
        } else {
            params[item] = parentId?.toLowerCase();
        }
    });

    return params;
};
